import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import {
  AccessBlockedPanel,
  RolesAccessPageLayout,
  RolesAccessSection,
} from '@/components/custom-roles';
import {
  CustomRoleForm,
  emptyCustomRoleFormValues,
} from '@/components/custom-roles/forms/CustomRoleForm';
import { CustomRoleFormValues } from '@/components/custom-roles/forms/validations/custom-roles';
import { useAccount, useCustomRole, useSaveCustomRole } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { useTrialRestriction } from '@/hooks/useTrialRestriction';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  customRoleToPermissionLevels,
  permissionLevelsToPermissions,
} from '@/utils/custom-roles';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountHeader } from './AccountHeader';

const getPathItems = (
  account: Account,
  currentAccount: Account,
  title: string,
  t: TranslationFunctionType,
): MenuItem[] => [
  {
    label: currentAccount?.isSystem ? t('accounts') : t('account'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS]()
      : RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('account.rolesAccess.tab'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_ROLES_ACCESS](account?.id)
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_ROLES_ACCESS](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: title,
    template: AppBreadCrumbTemplate,
  },
];

export const AccountRolesAccessBuilderPage: React.FC = () => {
  const { t } = useTranslation();
  const { id, roleId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { accountCanUseFeature } = useFeatureFlag();

  const accountId = (
    currentAccount?.isSystem ? id : currentAccount?.id
  ) as string;
  const { account, isLoading } = useAccount({ accountId });
  const viewedAccount = currentAccount?.isSystem ? account : currentAccount;

  const isTrialLocked = useTrialRestriction(viewedAccount);

  const hasCustomRoles = accountCanUseFeature(
    PostHogFeatureFlags.CUSTOM_ROLES,
    viewedAccount?.featureFlagAbility ?? [],
  );

  const { customRole, isLoading: isRoleLoading } = useCustomRole(
    isTrialLocked || !hasCustomRoles ? '' : accountId,
    roleId,
  );
  const { save, isLoading: isSaving } = useSaveCustomRole();

  const toList = () =>
    navigate(
      currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_ROLES_ACCESS](accountId)
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_ROLES_ACCESS](),
    );

  const handleSubmit = async (values: CustomRoleFormValues) => {
    try {
      await save({
        accountId,
        id: roleId,
        payload: {
          name: values.name,
          scopeMode: values.scopeMode,
          permissions: permissionLevelsToPermissions(values.permissions),
        },
      });

      toast?.success(
        t('toast.success'),
        roleId
          ? t('account.rolesAccess.builder.updated')
          : t('account.rolesAccess.builder.created'),
      );
      toList();
    } catch (e) {
      handleAxiosError(e, toast);
    }
  };

  const title = roleId
    ? t('account.rolesAccess.builder.title.edit')
    : t('account.rolesAccess.builder.title.create');

  if (isLoading || !account || !currentAccount || (!!roleId && isRoleLoading)) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
      </FlexContainer>
    );
  }

  return (
    <RolesAccessPageLayout>
      <AppBreadCrumb model={getPathItems(account, currentAccount, title, t)} />
      <AccountHeader
        selectedAccount={account}
        title={t('account.rolesAccess.tab')}
      />

      {!hasCustomRoles ? (
        <AccessBlockedPanel variant="no-access" />
      ) : isTrialLocked ? (
        <AccessBlockedPanel
          variant="free-trial"
          onBookMeeting={() =>
            navigate(RedirectPaths[RedirectPathsEnum.HUBSPOT_BOOKING]())
          }
        />
      ) : (
        <RolesAccessSection title={title}>
          <CustomRoleForm
            initialValues={
              customRole
                ? {
                    name: customRole.customRole.name,
                    scopeMode: customRole.customRole.scopeMode ?? null,
                    permissions: customRoleToPermissionLevels(
                      customRole.customRole,
                    ),
                  }
                : emptyCustomRoleFormValues()
            }
            assignedUserCount={customRole?.assignedUserCount ?? 0}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onCancel={toList}
          />
        </RolesAccessSection>
      )}
    </RolesAccessPageLayout>
  );
};
